import React, { useRef } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Cpu, CheckCircle2, Shield, TrendingUp, Zap, Brain, Bot } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

export default function OperationalExcellenceSection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-100px' });
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Parallax effects
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);
  const rotate = useTransform(scrollYProgress, [0, 1], [0, 360]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const principles = [
    { icon: TrendingUp, label: t('opExcellence.principle1'), color: '#8DC21F' },
    { icon: CheckCircle2, label: t('opExcellence.principle2'), color: '#3b82f6' },
    { icon: Shield, label: t('opExcellence.principle3'), color: '#f59e0b' },
    { icon: Cpu, label: t('opExcellence.principle4'), color: '#10b981' },
  ];

  const initiatives = [
    { name: 'PANDO', desc: t('opExcellence.initiative1Desc'), tag: 'AI', icon: Brain, color: '#8DC21F', link: '/pando' },
    { name: 'NeuralTrust', desc: t('opExcellence.initiative2Desc'), tag: 'Governance', icon: Shield, color: '#3b82f6' },
    { name: 'Robotics', desc: t('opExcellence.initiative3Desc'), tag: 'Field', icon: Bot, color: '#f59e0b' },
  ];

  return (
    <section ref={ref} className="py-20 sm:py-28 bg-gray-50 dark:bg-transparent relative overflow-hidden">
      {/* Animated tech grid background */}
      <motion.div 
        style={{ opacity }}
        className="absolute inset-0 pointer-events-none"
      >
        <div className="absolute inset-0 opacity-5" style={{ 
          backgroundImage: 'linear-gradient(rgba(141,194,31,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(141,194,31,0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
        <motion.div 
          style={{ y, rotate }}
          className="absolute top-20 right-20 w-32 h-32 opacity-10"
        >
          <Zap size={128} className="text-[#8DC21F]" />
        </motion.div>
      </motion.div>

      <div className="v-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <motion.span 
            className="v-tag green mb-6 inline-flex"
            whileHover={{ scale: 1.1, rotate: [0, -5, 5, 0] }}
            transition={{ duration: 0.5 }}
          >
            {t('opExcellence.tag')}
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black dark:text-white/95 mb-4 tracking-tight">
            {t('opExcellence.h1a')}{' '}
            <span style={{ fontFamily: "'Instrument Serif', serif", fontStyle: 'italic' }}>
              {t('opExcellence.h1b')}
            </span>
          </h2>
          <p className="text-base sm:text-lg text-gray-700 dark:text-white/65 max-w-3xl mx-auto leading-relaxed">
            {t('opExcellence.sub')}
          </p>
        </motion.div>

        {/* Principles with 3D-like effects */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-12">
          {principles.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 50, rotateY: -30 }}
                animate={inView ? { opacity: 1, y: 0, rotateY: 0 } : {}}
                transition={{ 
                  duration: 0.8, 
                  delay: i * 0.1,
                  type: "spring",
                  stiffness: 80
                }}
                whileHover={{ 
                  y: -10, 
                  scale: 1.08,
                  rotateY: 10,
                  rotateX: -5
                }}
                className="liquid-glass rounded-2xl p-6 flex flex-col items-center text-center group cursor-pointer relative overflow-hidden"
                style={{ transformStyle: 'preserve-3d' }}
              >
                {/* Animated background pulse */}
                <motion.div
                  className="absolute inset-0"
                  animate={{ 
                    background: [
                      `radial-gradient(circle at 50% 50%, ${p.color}00 0%, transparent 70%)`,
                      `radial-gradient(circle at 50% 50%, ${p.color}15 0%, transparent 70%)`,
                      `radial-gradient(circle at 50% 50%, ${p.color}00 0%, transparent 70%)`,
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />

                <motion.div 
                  className="w-12 h-12 rounded-xl mb-3 flex items-center justify-center relative z-10" 
                  style={{ background: `${p.color}12` }}
                  whileHover={{ 
                    scale: 1.3,
                    rotate: [0, 10, -10, 0],
                    transition: { duration: 0.6 }
                  }}
                >
                  <Icon size={22} style={{ color: p.color }} />
                </motion.div>
                <p className="text-sm font-medium text-black dark:text-white/80 relative z-10">{p.label}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Initiatives with card flip effects */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {initiatives.map((init, i) => {
            const Icon = init.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 60, scale: 0.9 }}
                animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                transition={{ 
                  duration: 0.8, 
                  delay: 0.4 + i * 0.15,
                  type: "spring"
                }}
                whileHover={{ 
                  y: -12,
                  scale: 1.03,
                  transition: { duration: 0.3 }
                }}
                className="liquid-glass rounded-3xl p-6 group relative overflow-hidden"
                style={{ cursor: init.link ? 'pointer' : 'default' }}
                onClick={init.link ? () => navigate(init.link) : undefined}
              >
                {/* Gradient overlay on hover */}
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100"
                  style={{ background: `linear-gradient(135deg, ${init.color}08 0%, transparent 100%)` }}
                  transition={{ duration: 0.4 }}
                />

                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-4">
                    <motion.div
                      whileHover={{ rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <Icon size={32} style={{ color: init.color }} />
                    </motion.div>
                    <span 
                      className="text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full" 
                      style={{ background: `${init.color}15`, color: init.color }}
                    >
                      {init.tag}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-black dark:text-white/90 mb-2">{init.name}</h3>
                  <p className="text-sm text-gray-700 dark:text-white/65 leading-relaxed">{init.desc}</p>
                </div>

                {/* Shine effect on hover */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '100%' }}
                  transition={{ duration: 0.8 }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
